import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Alert, Typography, Row, Col, Card, Progress, Tag, Modal, List as AntList, Space, Collapse } from 'antd';
import {
  InfoCircleOutlined,
  ApartmentOutlined, // For Structure
  SettingOutlined, // For Function
  WarningOutlined, // For Risk
  FilterOutlined,
  FireOutlined,
  SafetyOutlined,
  HeartOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons';
import usePortalHealthScoring from '../hooks/usePortalHealthScoring';
import ParameterModal from './shared/ParameterModal';
import { getHealthScoreColor } from '../utils/healthColorUtils';
import { MAX_Z_FOR_ZERO_HEALTH } from '../utils/scoringEngineV2';
import { formatParameterValue, getDualUnitInfo } from '../utils/unitConversions';
import SVGCoxcombChart from './SVGCoxcombChart';
import UnifiedHealthWellnessDashboard from './UnifiedHealthWellnessDashboard';
import NutritionParameterSummary from './NutritionParameterSummary';
import MovementParameterSummary from './MovementParameterSummary';
import CompactParameterModal from './CompactParameterModal';
import { calculateLevinePhenoAge, calculatePREVENTScore } from '../utils/riskCalculators';

const { Title, Text, Paragraph } = Typography;

/**
 * Custom hook to handle responsive window width
 * Prevents infinite re-renders by properly managing window resize events
 */
const useResponsiveWidth = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 575;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 575);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const systemIcons = {
  Neurological: <img src="/icons/Neurological icon.png" alt="Neurological" />,
  Cardiovascular: <img src="/icons/Cardiovascular icon.png" alt="Cardiovascular" />,
  Respiratory: <img src="/icons/Respiratory icon.png" alt="Respiratory" />,
  Metabolic: <img src="/icons/Metabolic icon.png" alt="Metabolic" />,
  Filtration: <img src="/icons/Filtration icon.png" alt="Filtration" />,
  Musculoskeletal: <img src="/icons/Musculoskeletal icon.png" alt="Musculoskeletal" />,
  Immune: <img src="/icons/Immune icon.png" alt="Immune" />,
};

const sfrIcons = {
  Structure: <ApartmentOutlined />,
  Function: <SettingOutlined />,
  Risk: <WarningOutlined />,
};

const lifestyleIcons = {
  Nutrition: <img src="/icons/Nutrition icon.png" alt="Nutrition" />,
  Movement: <img src="/icons/Movement icon.png" alt="Movement" />,
  Recovery: <img src="/icons/Recovery icon.png" alt="Recovery" />,
  'Mental Health': <img src="/icons/Emotion icon.png" alt="Mental Health" />,
  Environment: <img src="/icons/Environment icon.png" alt="Environment" />,
};

const NewPatientPortalHealthOverview = ({ previewData, isStaffPreview }) => {
  // Use responsive hook to prevent infinite re-renders
  const isMobile = useResponsiveWidth();

  const {
    loading,
    error,
    tableData,
    overallDeviation,
    systemDeviations,
    lifestyleDeviations,
    patientDemographics,
    allParamsConfig,
  } = usePortalHealthScoring(previewData);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContentData, setModalContentData] = useState({ sfrScores: null, factors: [] });

  // Calculate parameter counts for each health system
  const calculateParameterCountsPerSystem = useMemo(() => {
    if (!tableData || !Array.isArray(tableData) || !allParamsConfig) {
      return {};
    }

    const systemCounts = {
      cardiovascular: 0,
      respiratory: 0,
      metabolic: 0,
      neurological: 0,
      filtration: 0,
      musculoskeletal: 0,
      immune: 0
    };

    // Create a map of available parameters from tableData
    const availableParamsMap = new Map();
    tableData.forEach(categoryGroup => {
      (categoryGroup.parameters || []).forEach(param => {
        if (param.currentValue !== null && param.currentValue !== undefined) {
          availableParamsMap.set(param.key, param);
        }
      });
    });

    // Count parameters for each system based on parameter weights configuration
    Object.entries(allParamsConfig).forEach(([paramKey, paramConfig]) => {
      // Only count parameters that have patient data
      if (availableParamsMap.has(paramKey)) {
        // Check if parameter has system weights
        if (paramConfig.systems) {
          Object.keys(paramConfig.systems).forEach(systemKey => {
            const systemWeights = paramConfig.systems[systemKey];
            // Count parameter if it has meaningful contribution to the system
            // (any S, F, or R weight > 10 indicates meaningful contribution)
            if (systemWeights && (
              (systemWeights.S && systemWeights.S > 10) ||
              (systemWeights.F && systemWeights.F > 10) ||
              (systemWeights.R && systemWeights.R > 10) ||
              (systemWeights.structure && systemWeights.structure > 10) ||
              (systemWeights.function && systemWeights.function > 10) ||
              (systemWeights.risk && systemWeights.risk > 10)
            )) {
              if (systemCounts.hasOwnProperty(systemKey)) {
                systemCounts[systemKey]++;
              }
            }
          });
        }
      }
    });

    console.log('[NewPatientPortalHealthOverview] Parameter counts per system:', systemCounts);
    return systemCounts;
  }, [tableData, allParamsConfig]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin tip="Loading Health Overview..." size="large" /></div>;
  }

  if (error) {
    return <Alert message="Error Loading Health Overview" description={error} type="error" showIcon style={{ margin: 20 }} />;
  }

  const getStatusColor = (score) => {
    return getHealthScoreColor(score);
  };

  // Calculate biological age using available patient data
  const calculateBiologicalAge = () => {
    if (!tableData || !Array.isArray(tableData)) {
      return { error: 'No patient data available for biological age calculation.' };
    }

    // Extract required parameters for Levine PhenoAge calculation
    const getParameter = (paramKey) => {
      for (const category of tableData) {
        if (category.parameters && Array.isArray(category.parameters)) {
          const param = category.parameters.find(p => p.key === paramKey);
          if (param && param.currentValue !== null && param.currentValue !== undefined) {
            // The data hook should provide the units. We find the most recent entry to get its units.
            const mostRecentEntry = param.dataRetrievalDetails?.debugInfo?.hasMostRecent ? param.dataRetrievalDetails.debugInfo : null;
            const units = mostRecentEntry?.units || param.units; // Fallback to param.units if needed
            return { value: parseFloat(param.currentValue), units: units };
          }
        }
      }
      return { value: null, units: null };
    };

    // Get chronological age from patient demographics
    const chronologicalAge = patientDemographics?.birthDate
      ? Math.floor((new Date() - new Date(patientDemographics.birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
      : null;

    // Required parameters for PhenoAge calculation following parameters.md naming
    const albuminParam = getParameter('labs_protein_albumin');
    const creatinineParam = getParameter('labs_renal_creatinine');
    const glucoseParam = getParameter('labs_glucose_glucose');
    const crpParam = getParameter('labs_inflammation_hscrp');
    const lymphPctParam = getParameter('labs_cbc_lymph_pct');
    const mcvParam = getParameter('labs_cbc_mcv');
    const rdwParam = getParameter('labs_cbc_rdw');
    const alpParam = getParameter('labs_hepatology_alp');
    const wbcParam = getParameter('labs_cbc_wbc');

    const bioAgeParams = {
      chronologicalAge,
      albumin: albuminParam.value,
      creatinine: creatinineParam.value,
      glucose: glucoseParam.value,
      glucoseUnits: glucoseParam.units,
      cReactiveProtein: crpParam.value,
      lymphocytePercent: lymphPctParam.value,
      meanCellVolume: mcvParam.value,
      redCellDistributionWidth: rdwParam.value,
      alkalinePhosphatase: alpParam.value,
      whiteBloodCellCount: wbcParam.value
    };

    // Check which required parameters are missing
    const missingBioAgeParams = Object.entries({
      'Chronological Age': chronologicalAge,
      'Albumin': albuminParam.value,
      'Creatinine': creatinineParam.value,
      'Glucose': glucoseParam.value,
      'C-Reactive Protein': crpParam.value,
      'Lymphocyte %': lymphPctParam.value,
      'Mean Cell Volume': mcvParam.value,
      'Red Cell Distribution Width': rdwParam.value,
      'Alkaline Phosphatase': alpParam.value,
      'White Blood Cell Count': wbcParam.value
    }).filter(([, value]) => value === null || value === undefined).map(([key]) => key);

    if (missingBioAgeParams.length > 0) {
      return { error: `Missing required parameters: ${missingBioAgeParams.join(', ')}.` };
    }

    try {
      return calculateLevinePhenoAge(bioAgeParams);
    } catch (error) {
      console.error('Biological age calculation error:', error);
      return { error: 'Error calculating biological age' };
    }
  };

  const biologicalAgeResult = calculateBiologicalAge();

  // Debug logging to understand data availability
  console.log('🔍 Health Overview Debug Info:');
  console.log('- tableData:', tableData?.length || 0, 'categories');
  console.log('- patientDemographics:', patientDemographics);
  console.log('- biologicalAgeResult:', biologicalAgeResult);
  console.log('- preventScoreResult will be calculated next...');

  // Calculate PREVENT score using available patient data
  const calculatePreventScoreResult = () => {
    if (!tableData || !Array.isArray(tableData) || !patientDemographics) {
      return { error: 'Insufficient patient data for PREVENT Score calculation.' };
    }

    const getParameterValue = (paramKey) => {
      for (const category of tableData) {
        if (category.parameters && Array.isArray(category.parameters)) {
          const param = category.parameters.find(p => p.key === paramKey);
          if (param && param.currentValue !== null && param.currentValue !== undefined) {
            return parseFloat(param.currentValue);
          }
        }
      }
      return null;
    };

    const params = {
      age: patientDemographics.age,
      sex: patientDemographics.sex,
      totalCholesterol: getParameterValue('labs_lipid_chol_total'),
      hdlCholesterol: getParameterValue('labs_lipid_hdl'),
      systolicBP: getParameterValue('vitals_sbp'),
      bmi: getParameterValue('vitals_bmi'),
      eGFR: getParameterValue('labs_renal_egfr'),
      onBPMeds: patientDemographics.onBPMeds,
      onStatin: patientDemographics.onStatin,
      diabetes: patientDemographics.diabetes,
      smoker: patientDemographics.smoker,
    };

    // The calculatePREVENTScore function already handles missing/invalid params internally
    const result = calculatePREVENTScore(params);
    return { ...result, params };
  };

  const preventScoreResult = calculatePreventScoreResult();
  console.log('- preventScoreResult:', preventScoreResult);

  const renderPreventScoreCard = () => {
    // Always show the card, either with data or error state
    if (!preventScoreResult) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>🛡️</span>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>PREVENT Score Assessment</span>
            </div>
          }
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e8e8e8',
            marginBottom: '20px'
          }}
        >
          <Alert
            message="Heart Health Assessment Unavailable"
            description="Unable to load patient data for PREVENT Score calculation."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Card>
      );
    }

    if (preventScoreResult.error) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>🛡️</span>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>PREVENT Score Assessment</span>
            </div>
          }
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e8e8e8',
            marginBottom: '20px'
          }}
        >
          <Alert
            message="Heart Health Assessment Unavailable"
            description="We need more lab results to calculate your heart disease risk. This requires several tests that measure your cardiovascular health."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{
            background: '#e6f7ff',
            padding: '16px',
            borderRadius: '6px',
            border: '1px solid #91d5ff'
          }}>
            <Text strong style={{ color: '#0050b3', fontSize: '14px' }}>
              💡 What is PREVENT Score?
            </Text>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#0050b3' }}>
              Your PREVENT Score predicts your 10-year risk of heart disease and stroke by analyzing:
            </div>
            <div style={{ marginTop: '12px' }}>
              <Row gutter={[12, 8]}>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <HeartOutlined style={{ color: '#f5222d', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Blood Pressure (Systolic/Diastolic)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ExperimentOutlined style={{ color: '#fa8c16', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Cholesterol Levels (Total, LDL, HDL)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FireOutlined style={{ color: '#faad14', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Blood Sugar (Diabetes risk factors)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MedicineBoxOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Lifestyle & Medications</Text>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Card>
      );
    }

    const { score, category, color, ascvdRisk, heartFailureRisk, coronaryHeartDiseaseRisk, strokeRisk } = preventScoreResult;
    const riskColor = color === 'green' ? '#52c41a' : color === 'orange' ? '#fa8c16' : '#ff4d4f';

    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', marginRight: '8px' }}>🛡️</span>
            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>PREVENT Score Assessment</span>
          </div>
        }
        style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e8e8e8',
          marginBottom: '20px'
        }}
      >
        {/* Patient Education Section */}
        <div style={{
          background: '#e6f7ff',
          padding: '16px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: '1px solid #91d5ff'
        }}>
          <Text strong style={{ color: '#0050b3', fontSize: '14px' }}>
            💡 Your PREVENT Score predicts your 10-year risk of heart disease and stroke by analyzing:
          </Text>
          <Row gutter={[12, 8]} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <HeartOutlined style={{ color: '#f5222d', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Blood Pressure (Systolic/Diastolic)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ExperimentOutlined style={{ color: '#fa8c16', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Cholesterol Levels (Total, LDL, HDL)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FireOutlined style={{ color: '#faad14', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Blood Sugar (Diabetes risk factors)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MedicineBoxOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Lifestyle & Medications</Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Main PREVENT Score Display */}
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: riskColor,
                marginBottom: '8px'
              }}>
                {score.toFixed(1)}%
              </div>
              <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                10-Year CVD Risk
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                PREVENT Score (AHA 2023)
              </div>
              <div style={{ marginTop: 12 }}>
                <Tag
                  color={color}
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {category}
                </Tag>
              </div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>What This Means:</Title>
              <Paragraph style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                Your {score.toFixed(1)}% risk means that out of 100 people with similar health profiles, about {Math.round(score)} would develop heart disease or stroke in the next 10 years.
              </Paragraph>
              <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>Health Impact:</Title>
              <Paragraph style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                {score < 5
                  ? "Your heart disease risk is low. Continue your healthy lifestyle to maintain this excellent cardiovascular health."
                  : score < 10
                    ? "Your heart disease risk is moderate. Focus on lifestyle improvements to reduce your risk."
                    : "Your heart disease risk is elevated. Work closely with your healthcare provider on recommended interventions."
                }
              </Paragraph>
            </div>
          </Col>
        </Row>

      </Card>
    );
  };





  // Helper function to get all parameters associated with a health system
  const getParametersForHealthSystem = (systemKey, allParamsConfig, tableData) => {
    const associatedParams = [];

    if (!allParamsConfig || !tableData) {
      return associatedParams;
    }

    // Create a map of available parameters from tableData for quick lookup
    const availableParamsMap = new Map();
    (tableData || []).forEach(categoryGroup => {
      (categoryGroup.parameters || []).forEach(param => {
        availableParamsMap.set(param.key, {
          ...param,
          // Use main category name to match Priority Cards categorization
          category: categoryGroup.categoryName || param.category || 'Uncategorized'
        });
      });
    });

    // Check all parameters in the configuration to see if they're associated with this system
    Object.entries(allParamsConfig).forEach(([paramKey, paramConfig]) => {
      if (paramConfig.systems && paramConfig.systems[systemKey]) {
        const systemWeights = paramConfig.systems[systemKey];

        // Check if this parameter has any non-zero weight for this system
        const hasSystemAssociation = Object.values(systemWeights).some(weight =>
          (typeof weight === 'number' && weight > 0) ||
          (typeof weight === 'object' && weight.final_weight > 0)
        );

        if (hasSystemAssociation) {
          const paramData = availableParamsMap.get(paramKey);
          if (paramData) {
            // Parameter has data available
            associatedParams.push({
              ...paramData,
              hasData: true,
              isAssociated: true
            });
          } else {
            // Parameter is associated but no data available
            associatedParams.push({
              key: paramKey,
              parameterName: paramConfig.displayName || paramKey,
              category: paramConfig.category || 'Uncategorized',
              hasData: false,
              isAssociated: true,
              currentValue: null,
              idealTarget: null,
              zScore: null,
              absoluteZScore: 0,
              weightedAbsoluteZScore: 0
            });
          }
        }
      }
    });

    return associatedParams;
  };

  const handleSystemDomainCardClick = (key, type) => {
    let title = `${key.charAt(0).toUpperCase() + key.slice(1)} - Health Details & Key Factors`;
    let sfrScores = null;
    let dataObject = null;

    if (type === 'system') {
        dataObject = systemDeviations;
    } else if (type === 'lifestyle') {
        dataObject = lifestyleDeviations;
        title = `${key.charAt(0).toUpperCase() + key.slice(1)} Domain - Key Factors`;
    }

    // Get SFR scores for systems
    if (type === 'system' && dataObject && dataObject[key]) {
        sfrScores = {};
        Object.entries(dataObject[key]).forEach(([dimKey, dimensionData]) => {
            sfrScores[dimKey] = dimensionData.healthScore;
        });
    }

    let allParams = [];

    if (type === 'system') {
      // Get ALL parameters associated with this health system
      allParams = getParametersForHealthSystem(key, allParamsConfig, tableData);
    } else if (type === 'lifestyle') {
      // For lifestyle domains, keep the existing logic for now
      (tableData || []).forEach(categoryGroup => {
        (categoryGroup.parameters || []).forEach(param => {
          // Check if parameter is in this lifestyle domain
          let belongsToSelectedDomain = false;
          if (dataObject && dataObject[key]) {
            (dataObject[key].contributingParams || []).forEach(lifestyleParam => {
              if (lifestyleParam.key === param.key) {
                belongsToSelectedDomain = true;
              }
            });
          }

          if (belongsToSelectedDomain) {
            allParams.push({
              ...param,
              category: param.subcategory || param.category || categoryGroup.categoryName || 'Uncategorized',
              hasData: true,
              isAssociated: true
            });
          }
        });
      });
    }

    // Process all parameters to create the display data
    const processedParams = allParams.map(param => {
      const absZ = typeof param.absoluteZScore === 'number' ? param.absoluteZScore : 0;
      const overallImpact = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;

      let priority = 'Normal';
      if (param.hasData && absZ >= 1.0) {
        priority = 'Low';
        if (overallImpact >= 2.0) priority = 'High';
        else if (overallImpact >= 1.0) priority = 'Moderate';
      } else if (!param.hasData) {
        priority = 'No Data';
      }

      return {
        key: `${param.category}-${param.key}`,
        name: param.parameterName,
        zScore: param.zScore,
        specificImpact: overallImpact,
        priority: priority,
        category: param.category,
        currentValue: param.currentValue,
        idealTarget: param.idealTarget,
        parameterKey: param.key,
        hasData: param.hasData
      };
    });

    // Group by priority and sort within each group
    const groupedFactors = processedParams.reduce((acc, factor) => {
      const { priority } = factor;
      if (!acc[priority]) {
        acc[priority] = [];
      }
      acc[priority].push(factor);
      return acc;
    }, {});

    // Sort within each priority group
    for (const priority in groupedFactors) {
      if (priority === 'High' || priority === 'Moderate' || priority === 'Low') {
        groupedFactors[priority].sort((a, b) => b.specificImpact - a.specificImpact);
      } else {
        groupedFactors[priority].sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    // Sort priority groups in a specific order
    const priorityOrder = {
      'High': 5,
      'Moderate': 4,
      'Low': 3,
      'Normal': 2,
      'No Data': 1
    };

    // Create a new ordered object with priorities in the correct order
    const orderedFactors = {};
    Object.keys(groupedFactors)
      .sort((a, b) => (priorityOrder[b] || 0) - (priorityOrder[a] || 0))
      .forEach(priority => {
        orderedFactors[priority] = groupedFactors[priority];
      });

    setModalTitle(title);
    setModalContentData({
        sfrScores,
        factors: orderedFactors
    });
    setModalVisible(true);
  };



  const renderBiologicalAgeCard = () => {
    // Always show the card, either with data or error state
    if (!biologicalAgeResult) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age Assessment</span>
            </div>
          }
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e8e8e8',
            marginBottom: '20px'
          }}
        >
          <Alert
            message="Biological Age Assessment Unavailable"
            description="Unable to load patient data for biological age calculation."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Card>
      );
    }

    if (biologicalAgeResult.error) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age Assessment</span>
            </div>
          }
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e8e8e8',
            marginBottom: '20px'
          }}
        >
          <Alert
            message="Biological Age Assessment Unavailable"
            description="We need comprehensive blood work to calculate your biological age. This requires several lab tests that measure how your body is aging at the cellular level."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{
            background: '#e6f7ff',
            padding: '16px',
            borderRadius: '6px',
            border: '1px solid #91d5ff'
          }}>
            <Text strong style={{ color: '#0050b3', fontSize: '14px' }}>
              💡 What is Biological Age?
            </Text>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#0050b3' }}>
              Your biological age analyzes four key health systems:
            </div>
            <div style={{ marginTop: '12px' }}>
              <Row gutter={[12, 8]}>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FilterOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Filtration (Kidney Function)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FireOutlined style={{ color: '#fa8c16', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Metabolic (Energy Processing)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SafetyOutlined style={{ color: '#52c41a', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Immune (Disease Resistance)</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <HeartOutlined style={{ color: '#f5222d', marginRight: '6px' }} />
                    <Text style={{ fontSize: '12px' }}>Cardiovascular (Heart/Circulation)</Text>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Card>
      );
    }

    const { phenoAge, chronologicalAge, ageDifference, interpretation, mortalityScore } = biologicalAgeResult;
    const isYounger = ageDifference < 0;
    const ageColor = isYounger ? '#52c41a' : ageDifference > 5 ? '#ff4d4f' : ageDifference > 2 ? '#fa8c16' : '#faad14';

    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age Assessment</span>
          </div>
        }
        style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e8e8e8',
          marginBottom: '20px'
        }}
      >
        {/* Patient Education Section */}
        <div style={{
          background: '#e6f7ff',
          padding: '16px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: '1px solid #91d5ff'
        }}>
          <Text strong style={{ color: '#0050b3', fontSize: '14px' }}>
            💡 Your Biological Age analyzes four key health systems:
          </Text>
          <Row gutter={[12, 8]} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FilterOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Filtration (Kidney Function)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FireOutlined style={{ color: '#fa8c16', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Metabolic (Energy Processing)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SafetyOutlined style={{ color: '#52c41a', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Immune (Disease Resistance)</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <HeartOutlined style={{ color: '#f5222d', marginRight: '6px' }} />
                <Text style={{ fontSize: '12px' }}>Cardiovascular (Heart/Circulation)</Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Main Biological Age Display */}
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: ageColor,
                marginBottom: '8px'
              }}>
                {phenoAge.toFixed(2)}
              </div>
              <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                Biological Age (years)
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                vs. {chronologicalAge.toFixed(2)} actual age
              </div>
              <div style={{ marginTop: 12 }}>
                <Tag
                  color={isYounger ? 'green' : ageDifference > 5 ? 'red' : ageDifference > 2 ? 'orange' : 'gold'}
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {ageDifference > 0 ? '+' : ''}{ageDifference.toFixed(1)} years
                </Tag>
              </div>

              {/* Mort Score Display */}
              {mortalityScore !== undefined && (
                <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
                    Mortality Risk Score: {(mortalityScore * 1000).toFixed(3)}‰
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: '1.4' }}>
                    This score predicts mortality risk based on biological aging markers. Lower scores indicate better health outcomes.
                  </div>
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>What This Means:</Title>
              <Paragraph style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                {interpretation}
              </Paragraph>
              <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>Health Impact:</Title>
              <Paragraph style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                {isYounger
                  ? "Your body is aging slower than expected - keep up the great work with your health habits!"
                  : ageDifference > 5
                    ? "Your body shows signs of accelerated aging. Focus on the health improvements suggested above."
                    : "Your biological age is close to your actual age, which is normal for most people."
                }
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  // Helper function to create mobile-friendly labels
  const getMobileLabel = (fullLabel) => {
    const mobileLabels = {
      'Neurological': 'Neuro',
      'Cardiovascular': 'Cardio',
      'Respiratory': 'Respir',
      'Metabolic': 'Metab',
      'Musculoskeletal': 'Muscle',
      'Filtration': 'Filter',
      'Immune': 'Immune',
      'Mental Health': 'Mental',
      'Nutrition': 'Nutri',
      'Movement': 'Move',
      'Recovery': 'Recov',
      'Environment': 'Enviro'
    };
    return mobileLabels[fullLabel] || fullLabel;
  };

  const renderHealthSystemCards = () => {
    return (
      <Card title={<Title level={4} style={{color: '#34495e', marginBottom:0}}>Health Systems at a Glance</Title>} style={{marginBottom: 24, background: '#f9f9f9' }}>
        <Row gutter={[16, 16]}>
          {Object.entries(systemDeviations || {}).map(([sysKey, dimensions]) => {
            let totalSystemScore = 0;
            let dimensionCount = 0;
            Object.values(dimensions).forEach(dimData => {
              if (dimData.healthScore !== null && dimData.healthScore !== undefined) {
                totalSystemScore += dimData.healthScore;
                dimensionCount++;
              }
            });
            const averageSystemHealth = dimensionCount > 0 ? Math.round(totalSystemScore / dimensionCount) : 0;
            const normalizedSysKey = sysKey.charAt(0).toUpperCase() + sysKey.slice(1).toLowerCase();
            const SpecificIcon = systemIcons[normalizedSysKey] || <InfoCircleOutlined />;

            // Get parameter count for this system
            const parameterCount = calculateParameterCountsPerSystem[sysKey.toLowerCase()] || 0;

            return (
              <Col xs={12} sm={8} md={6} lg={6} key={sysKey}>
                <Card
                    hoverable
                    onClick={() => handleSystemDomainCardClick(sysKey, 'system')}
                    style={{ textAlign: 'center', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)', height: '100%' }}
                    styles={{body: {padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '160px'}}}
                >
                  <Space direction="vertical" align="center" size="small" style={{ width: '100%' }}>
                    <span style={{ fontSize: '32px', color: getStatusColor(averageSystemHealth), lineHeight: 1, display: 'inline-block', width: '32px', height: '32px' }}>
                      {SpecificIcon ? React.cloneElement(SpecificIcon, { style: { width: '100%', height: '100%', objectFit: 'contain' }}) : <InfoCircleOutlined style={{ fontSize: '32px' }} />}
                    </span>
                    <Progress
                        type="circle"
                        percent={averageSystemHealth}
                        format={(percent) => <Text style={{fontSize: '14px', fontWeight: 'bold', color: getStatusColor(averageSystemHealth)}}>{`${percent}%`}</Text>}
                        strokeColor={getStatusColor(averageSystemHealth)}
                        size={70}
                        trailColor="#f0f2f5"
                        strokeWidth={5}
                    />
                    <Text strong style={{
                      fontSize: '11px',
                      color: '#595959',
                      textAlign: 'center',
                      lineHeight: '1.2',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }} title={normalizedSysKey}>
                      {isMobile ? getMobileLabel(normalizedSysKey) : normalizedSysKey}
                    </Text>

                    {/* Parameter Count Display */}
                    <Text style={{
                      fontSize: '10px',
                      color: '#999',
                      marginTop: '4px'
                    }}>
                      {parameterCount} parameters
                    </Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  const renderLifestyleDomainCards = () => {
    return (
      <Card title={<Title level={4} style={{color: '#34495e', marginBottom:0}}>Lifestyle Domains at a Glance</Title>} style={{marginBottom: 24, background: '#f9f9f9'}}>
        <Row gutter={[16, 16]}>
          {Object.entries(lifestyleDeviations || {}).map(([domainKey, data]) => {
            const domainHealthScore = data.healthScore !== null && data.healthScore !== undefined ? Math.round(data.healthScore) : 0;

            let normalizedDomainKey = domainKey.charAt(0).toUpperCase() + domainKey.slice(1).toLowerCase();
            if (normalizedDomainKey.toLowerCase().replace(/[\s_]/g, "") === "mentalhealth") {
              normalizedDomainKey = "Mental Health";
            } else {
              normalizedDomainKey = normalizedDomainKey.replace("_", " ");
              normalizedDomainKey = normalizedDomainKey.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
            }

            const domainIconElement = lifestyleIcons[normalizedDomainKey] || <InfoCircleOutlined />;

            return (
              <Col xs={12} sm={8} md={6} lg={6} key={domainKey}>
                <Card
                    hoverable
                    onClick={() => handleSystemDomainCardClick(domainKey, 'lifestyle')}
                    style={{ textAlign: 'center', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)', height: '100%' }}
                    styles={{body: {padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '160px'}}}
                >
                  <Space direction="vertical" align="center" size="small" style={{ width: '100%' }}>
                    <span style={{ fontSize: '32px', color: getStatusColor(domainHealthScore), lineHeight: 1, display: 'inline-block', width: '32px', height: '32px' }}>
                     {React.isValidElement(domainIconElement) ? React.cloneElement(domainIconElement, { style: { width: '100%', height: '100%', objectFit: 'contain' }}) : <InfoCircleOutlined style={{ fontSize: '32px' }} />}
                    </span>
                     <Progress
                        type="circle"
                        percent={domainHealthScore}
                        format={(percent) => <Text style={{fontSize: '14px', fontWeight: 'bold', color: getStatusColor(domainHealthScore)}}>{`${percent}%`}</Text>}
                        strokeColor={getStatusColor(domainHealthScore)}
                        size={70}
                        trailColor="#f0f2f5"
                        strokeWidth={5}
                    />
                    <Text strong style={{
                      fontSize: '11px',
                      color: '#595959',
                      textAlign: 'center',
                      lineHeight: '1.2',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }} title={normalizedDomainKey}>
                      {isMobile ? getMobileLabel(normalizedDomainKey) : normalizedDomainKey}
                    </Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  // Transform system deviations data for SVGCoxcombChart
  const transformSystemDataForCoxcomb = () => {
    if (!systemDeviations || Object.keys(systemDeviations).length === 0) return [];

    return Object.entries(systemDeviations).map(([sysKey, dimensions]) => {
      let totalSystemScore = 0;
      let dimensionCount = 0;
      Object.values(dimensions).forEach(dimData => {
        if (dimData.healthScore !== null && dimData.healthScore !== undefined) {
          totalSystemScore += dimData.healthScore;
          dimensionCount++;
        }
      });
      const averageSystemHealth = dimensionCount > 0 ? Math.round(totalSystemScore / dimensionCount) : 0;
      const normalizedSysKey = sysKey.charAt(0).toUpperCase() + sysKey.slice(1).toLowerCase();

      return {
        name: normalizedSysKey,
        score: averageSystemHealth
      };
    });
  };

  // Transform lifestyle deviations data for SVGCoxcombChart
  const transformLifestyleDataForCoxcomb = () => {
    if (!lifestyleDeviations || Object.keys(lifestyleDeviations).length === 0) return [];

    return Object.entries(lifestyleDeviations).map(([domainKey, data]) => {
      const domainHealthScore = data.healthScore !== null && data.healthScore !== undefined ? Math.round(data.healthScore) : 0;

      let normalizedDomainKey = domainKey.charAt(0).toUpperCase() + domainKey.slice(1).toLowerCase();
      if (normalizedDomainKey.toLowerCase().replace(/[\s_]/g, "") === "mentalhealth") {
        normalizedDomainKey = "Mental Health";
      } else {
        normalizedDomainKey = normalizedDomainKey.replace("_", " ");
        normalizedDomainKey = normalizedDomainKey.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
      }

      return {
        name: normalizedDomainKey,
        score: domainHealthScore
      };
    });
  };

  const systemChartData = transformSystemDataForCoxcomb();
  const lifestyleChartData = transformLifestyleDataForCoxcomb();

  return (
    <div style={{ padding: '24px', background: '#eceff1' }}>
      {patientDemographics && (
        <Paragraph style={{ marginBottom: 20, fontSize: '16px', background: '#fff', padding: '10px 15px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}}>
          <Text strong>{patientDemographics.firstName || ''} {patientDemographics.lastName || ''}</Text>
        </Paragraph>
      )}

      {/* Unified Health Wellness Dashboard - Combines Overall Wellness Score and Parameter Summary */}
      <UnifiedHealthWellnessDashboard
        loading={loading}
        tableData={tableData}
        overallDeviation={overallDeviation}
      />

      {/* Biological Age Assessment */}
      {renderBiologicalAgeCard()}

      {/* PREVENT Score Assessment */}
      {renderPreventScoreCard()}

      {/* Interactive Coxcomb Charts Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card
            title={<Title level={4} style={{color: '#34495e', marginBottom:0}}>📊 Health Systems & Lifestyle Overview - Interactive Charts</Title>}
            style={{ background: '#fdfdfd', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          >

            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <SVGCoxcombChart
                  healthSystemsData={systemChartData}
                  lifestyleDomainsData={[]}
                  title="Health Systems Overview"
                  chartType="systems"
                  systemDeviations={systemDeviations}
                  lifestyleDeviations={lifestyleDeviations}
                />
              </Col>
              <Col xs={24} md={12}>
                <SVGCoxcombChart
                  healthSystemsData={[]}
                  lifestyleDomainsData={lifestyleChartData}
                  title="Lifestyle Balance Overview"
                  chartType="lifestyle"
                  systemDeviations={systemDeviations}
                  lifestyleDeviations={lifestyleDeviations}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          {renderHealthSystemCards()}
        </Col>
        <Col xs={24} lg={12}>
          {renderLifestyleDomainCards()}
        </Col>
      </Row>

      {modalTitle.includes("Nutrition") ? (
        <Modal
          title={modalTitle}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width="90vw"
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          <NutritionParameterSummary factors={Object.values(modalContentData.factors || {}).flat()} />
        </Modal>
      ) : modalTitle.includes("Movement") ? (
        <Modal
          title={modalTitle}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width="90vw"
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          <MovementParameterSummary factors={Object.values(modalContentData.factors || {}).flat()} />
        </Modal>
      ) : (
        <CompactParameterModal
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          title={modalTitle}
          parametersData={modalContentData.factors}
          sfrScores={modalContentData.sfrScores}
        />
      )}
    </div>
  );
};

export default NewPatientPortalHealthOverview;
